// Gemini AI service for contract analysis using Vertex AI for Firebase
import { getGenerativeModel } from 'firebase/ai';
import { vertexAI } from '../lib/firebase';
import { Contractor, Contract } from '../types';

export interface FitReport {
    score: number; // 0-100
    summary: string;
    pros: string[];
    cons: string[];
    redFlags: string[];
    requirements: {
        personnelCount?: number;
        clearanceLevel?: string;
        location?: string;
        deadline?: string;
    };
    recommendation: 'bid' | 'review' | 'pass';
    analyzed: boolean;
    error?: string;
}

// Build the analysis prompt
function buildAnalysisPrompt(contract: Contract, contractor: Contractor): string {
    return `You are a government contracting analyst. Analyze this federal contract opportunity for the contractor and provide a fit assessment.

## Contractor Profile
- **Company:** ${contractor.name}
- **NAICS Codes:** ${contractor.naicsCodes.join(', ')}
- **Keywords:** ${contractor.keywords.join(', ')}
${contractor.capabilityStatement ? `\n**Capability Statement:**\n${contractor.capabilityStatement}\n` : ''}
${contractor.personnel ? `\n**Resources:**
  - Count: ${contractor.personnel.count}
  - Clearances: ${contractor.personnel.clearances.join(', ') || 'None'}
  - Certifications: ${contractor.personnel.certifications.join(', ') || 'None'}` : ''}
${contractor.preferences ? `\n**Preferences:**
  - Min Value: $${contractor.preferences.minContractValue || 0}
  - Max Value: ${contractor.preferences.maxContractValue ? '$' + contractor.preferences.maxContractValue : 'No limit'}
  - Locations: ${contractor.preferences.preferredLocations?.join(', ') || 'Any'}` : ''}
${contractor.notes ? `- **Notes:** ${contractor.notes}` : ''}

## Contract Opportunity
- **Title:** ${contract.title}
- **Agency:** ${contract.agency || contract.subAgency || 'Unknown'}
- **NAICS:** ${contract.naicsCode || 'Not specified'}
- **Type:** ${contract.type || 'Not specified'}
- **Set-Aside:** ${contract.setAside || 'None'}
- **Posted:** ${contract.postedDate || 'Unknown'}
- **Description:** ${contract.description ? contract.description.slice(0, 3000) : 'No description provided'}

## Instructions
Analyze the fit between this contractor and contract. Consider the capability statement and resources carefully.
Return ONLY valid JSON (no markdown, no code blocks):

{
  "score": <number 0-100>,
  "summary": "<2-3 sentence summary of the opportunity>",
  "pros": ["<reason this is a good fit>", ...],
  "cons": ["<concern or challenge>", ...],
  "redFlags": ["<critical issue that may disqualify>", ...],
  "requirements": {
    "personnelCount": <number or null>,
    "clearanceLevel": "<string or null>",
    "location": "<string or null>",
    "deadline": "<string or null>"
  },
  "recommendation": "<'bid' if score >= 70, 'review' if 40-69, 'pass' if < 40>"
}

Scoring Guide:
- 80-100: Excellent fit, capabilities match specifically
- 60-79: Good fit, general alignment
- 40-59: Moderate fit, gaps exist
- 20-39: Poor fit, significant gaps
- 0-19: Not a fit`;
}

// Call Gemini API via Vertex AI
export async function analyzeContract(
    contract: Contract,
    contractor: Contractor
): Promise<FitReport> {
    try {
        // Initialize model
        const model = getGenerativeModel(vertexAI, {
            model: 'gemini-2.0-flash',
            generationConfig: {
                temperature: 0.2, // Lower temperature for more consistent JSON
                maxOutputTokens: 1024,
            }
        });

        const prompt = buildAnalysisPrompt(contract, contractor);

        // Generate content
        const result = await model.generateContent(prompt);
        const response = result.response;
        const text = response.text();

        // Clean and parse JSON
        let cleanText = text.trim();
        if (cleanText.startsWith('```json')) {
            cleanText = cleanText.slice(7);
        }
        if (cleanText.startsWith('```')) {
            cleanText = cleanText.slice(3);
        }
        if (cleanText.endsWith('```')) {
            cleanText = cleanText.slice(0, -3);
        }
        cleanText = cleanText.trim();

        const data = JSON.parse(cleanText);

        return {
            score: Math.min(100, Math.max(0, data.score || 0)),
            summary: data.summary || 'No summary available',
            pros: data.pros || [],
            cons: data.cons || [],
            redFlags: data.redFlags || [],
            requirements: data.requirements || {},
            recommendation: data.recommendation || 'review',
            analyzed: true
        };

    } catch (err: any) {
        console.error('Vertex AI error:', err);
        return {
            score: 0,
            summary: 'Analysis failed (Vertex AI)',
            pros: [],
            cons: [],
            redFlags: [],
            requirements: {},
            recommendation: 'review',
            analyzed: false,
            error: err.message || 'Unknown Vertex AI error'
        };
    }
}

// Heuristic Engine for Insights
function generateHeuristicInsights(contract: any, contractor: Contractor, fitScore: number): string[] {
    const insights: string[] = [];
    const text = (contract.title + ' ' + (contract.description || '')).toLowerCase();

    // 1. Incumbent Detection
    if (/\b(incumbent|current contractor|re-compete|follow-on|follow on)\b/.test(text)) {
        // Unless it says "no incumbent"
        if (!/\b(no incumbent|new requirement)\b/.test(text)) {
            insights.push('⚠️ High Risk: Incumbent language detected in description.');
        }
    }

    // 2. Funding Detection
    if (/\b(funded|funding available|budget approved|currently funded)\b/.test(text)) {
        insights.push('💰 Secure: Funding explicitly mentioned.');
    }

    // 3. New Work
    if (/\b(new requirement|new scope|no incumbent)\b/.test(text)) {
        insights.push('✅ Strong Opportunity: explicit "New Requirement".');
    }

    // 4. Tech/Keyword Match Explanation
    const matchedKeywords = contractor.keywords.filter(k => text.includes(k.toLowerCase()));
    if (matchedKeywords.length >= 2) {
        const topMatches = matchedKeywords.slice(0, 3).join(', ');
        insights.push(`Matches your expertise in: ${topMatches}.`);
    }

    // 5. Score Logic
    if (fitScore >= 90) insights.unshift('🔥 Excellent Fit: Aligns perfectly with your capability profile.');
    else if (fitScore >= 80) insights.unshift('✅ Good Fit: Strong alignment with primary keywords.');

    return insights;
}

// Generate a cohesive dynamic narrative based on the data
export function generateExecutiveSummary(contract: any, contractor: Contractor, fitScore: number, opportunityScore: number, insights: string[]): string {
    const agency = contract.agency || contract.subAgency || 'the agency';
    const text = (contract.title + ' ' + (contract.description || '')).toLowerCase();

    // Recalculate keywords for narrative usage
    const matchedKeywords = contractor.keywords.filter(k => text.includes(k.toLowerCase()));
    const topKeywords = matchedKeywords.slice(0, 4).join(', ');

    const isIncumbent = insights.some(i => i.includes('Incumbent'));
    const isFunded = insights.some(i => i.includes('Funding'));
    const isNew = insights.some(i => i.includes('New Requirement'));
    const isUrgent = insights.some(i => i.includes('Urgent'));

    let section1 = '';
    let section2 = '';
    let section3 = '';

    // --- Section 1: The Opportunity (What) ---
    section1 += `The Opportunity: This is a ${isNew ? 'newly identified ' : ''}solicitation from ${agency} for ${contract.title}. `;
    if (contract.typeOfSetAside) {
        section1 += `It is set aside for ${contract.typeOfSetAside}, restricting competition to eligible firms. `;
    }
    if (isFunded) {
        section1 += `Funding appears to be confirmed, which significantly reduces the risk of cancellation. `;
    } else {
        section1 += `The scope indicates a standard requirement for this agency's mission. `;
    }

    // --- Section 2: The Fit (Why Us) ---
    if (fitScore >= 80) {
        section2 += `The Fit: This opportunity is a Strong Match (${fitScore}%) for your profile. `;
    } else if (fitScore >= 60) {
        section2 += `The Fit: This opportunity aligns well (${fitScore}%) with your core competencies. `;
    } else {
        section2 += `The Fit: While there is some alignment (${fitScore}%), this may require a teaming partner. `;
    }

    if (topKeywords.length > 0) {
        section2 += `It specifically triggers your capabilities in ${topKeywords}. `;
    }

    // Naics check explanation
    if (contract.naicsCode && contractor.naicsCodes.includes(contract.naicsCode)) {
        section2 += `Furthermore, it operates under your primary NAICS code (${contract.naicsCode}), reinforcing your past performance relevance. `;
    }

    // --- Section 3: The Strategy (How to Win) ---
    section3 += `Strategic Approach: `;

    // Hallucinate/Infer Strategy based on Heuristics
    if (isIncumbent) {
        section3 += `Since an incumbent is present, your strategy must focus on innovation and cost-efficiency to displace the existing vendor. Highlights gaps in their current service delivery if known. `;
    } else if (isNew) {
        section3 += `As a New Requirement, the agency is looking for a partner to define the path forward. Position yourself as a thought leader and "trusted advisor" rather than just a vendor. `;
    }

    if (opportunityScore > 70) {
        section3 += `Given the high strategic value, we recommend assigning a dedicated Capture Manager immediately. `;
    }

    if (text.includes('idiq') || text.includes('vehicle')) {
        section3 += `Focus your proposal on breadth of capability and management capacity, as this is likely a vehicle for future task orders. `;
    } else if (text.includes('software') || text.includes('development')) {
        section3 += `Emphasize your Agile methodology and rapid prototyping capabilities to de-risk the technical execution for the agency. `;
    } else if (text.includes('staffing') || text.includes('support services')) {
        section3 += `Highlight your recruiting pipeline and retention strategy, as personnel risk is the primary concern for the CO. `;
    } else {
        section3 += `Ensure your response strictly adheres to the Section L instructions, as compliance is the first gate. `;
    }

    if (isUrgent) {
        section3 += `Urgent: With the deadline approaching, prioritize a "Compliance + 1" strategy—ensure full compliance first, then add value where time permits.`;
    }

    return `${section1}\n\n${section2}\n\n${section3}`;
}

// Generate a detailed "Deep Analysis" report (hallucinated if no docs)
export function generateDeepAnalysisReport(contract: any): string {
    const agency = contract.agency || 'The Agency';
    const title = contract.title || 'Contract Opportunity';

    return `
### 1. Executive Synopsis
This solicitation from ${agency} represents a critical requirement for ${title}. The government is seeking a vendor capable of delivering comprehensive solutions that align with their mission objectives. This report breaks down the key components of the opportunity to guide your bid decision.

### 2. Scope of Work Breakdown
Based on the solicitation metadata, the core scope involves:
- **Primary Objective**: Delivery of services/products related to ${title}.
- **Key Deliverables**: Anticipated requirements include management plans, technical execution, and regular performance reporting.
- **Operational Environment**: Work is expected to be performed in coordination with agency stakeholders, requiring strong communication protocols.

### 3. Evaluation Criteria (Inferred)
Agencies typically evaluate proposals for this type of requirement based on:
- **Technical Approach**: Demonstrated understanding of the problem space and a viable methodology.
- **Past Performance**: Relevance of previous experience to specific agency needs.
- **Price/Cost**: Realism and reasonableness of the proposed pricing structure.
- **Management Plan**: Ability to staff and manage the project with low risk.

### 4. Competitive Landscape
- **Incumbency**: Check for existing contracts (e.g., via FPDS) to identify incumbents. If present, expect a "Least Risk" evaluation bias.
- **Set-Aside Implications**: ${contract.typeOfSetAside ? `This is a ${contract.typeOfSetAside} set-aside, limiting the competitor pool.` : 'This is Full and Open competition, expect a crowded field.'}

### 5. Final Recommendation
Review the full solicitation documents (PWS/SOW) immediately. If the scope aligns with your capabilities as indicated by the Fit Score, proceed to the "Gate 1" review. Focus your proposal theme on "Risk Reduction" and "Mission Assurance".
    `.trim();
}

// Quick score (without full analysis) for batch processing
export async function quickScore(
    contract: Contract | any,
    contractor: Contractor
): Promise<{ fitScore: number, opportunityScore: number, actions: string[], insights: string[] }> {
    let fitScore = 50; // Base score
    let opportunityScore = 50; // Base opportunity score
    const actions: string[] = [];

    const titleLower = contract.title?.toLowerCase() || '';
    const descLower = contract.description?.toLowerCase() || '';

    // NAICS match
    if (contract.naicsCode && contractor.naicsCodes.includes(contract.naicsCode)) {
        fitScore += 20;
    }

    // Set-Aside match
    if (contract.typeOfSetAside && contractor.preferredSetAsides?.includes(contract.typeOfSetAside)) {
        fitScore += 15;
    }

    // Keyword match
    const matchingKeywords = contractor.keywords.filter(k =>
        titleLower.includes(k.toLowerCase())
    );
    fitScore += matchingKeywords.length * 10;

    const descKeywords = contractor.keywords.filter(k =>
        !titleLower.includes(k.toLowerCase()) && descLower.includes(k.toLowerCase())
    );
    fitScore += descKeywords.length * 5;

    // Exclude keyword penalty
    if (contractor.excludeKeywords) {
        const excludeMatches = contractor.excludeKeywords.filter(k =>
            titleLower.includes(k.toLowerCase()) || descLower.includes(k.toLowerCase())
        );
        fitScore -= excludeMatches.length * 20;
    }

    // --- OPPORTUNITY SCORE LOGIC ---
    // Bonus for specific set-asides
    if (contract.typeOfSetAside && ['8A', 'SDVOSB', 'WOSB', 'HZC'].includes(contract.typeOfSetAside)) {
        opportunityScore += 10;
    }

    // Bonus for "Solicitation"
    if (contract.type === 'Solicitation' || contract.type === 'Combined Synopsis/Solicitation') {
        opportunityScore += 10;
        actions.push('Review Solicitation Docs');
    } else if (contract.type === 'Sources Sought') {
        actions.push('Send Capabilities Statement');
    }

    // Heuristics for value
    if (titleLower.includes('idiq') || titleLower.includes('multiple award') || titleLower.includes('matoc')) {
        opportunityScore += 20;
        actions.push('Evaluate Vehicle Access');
    }

    // Check for deadline urgency
    if (contract.responseDeadline) {
        const days = Math.ceil((new Date(contract.responseDeadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
        if (days > 14) opportunityScore += 5; // Good lead time
        if (days < 5 && days > 0) {
            opportunityScore -= 5; // Tight turn around
        }
    }

    // Normalize
    fitScore = Math.min(99, Math.max(0, fitScore));
    opportunityScore = Math.min(99, Math.max(0, opportunityScore));

    if (fitScore > 60) actions.unshift('Prioritize Bid');

    // Generate Textual Insights
    const insights = generateHeuristicInsights(contract, contractor, fitScore);

    return { fitScore, opportunityScore, actions, insights };
}

// Get score color based on value
export function getScoreColor(score: number): string {
    if (score >= 60) return 'text-green-400';
    if (score >= 50) return 'text-blue-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
}

export function getScoreBgColor(score: number): string {
    if (score >= 60) return 'bg-green-500/10';
    if (score >= 50) return 'bg-blue-500/10';
    if (score >= 40) return 'bg-yellow-500/10';
    return 'bg-red-500/10';
}

export function getRecommendationLabel(rec: string): { label: string; color: string } {
    switch (rec) {
        case 'bid':
            return { label: 'Recommend Bid', color: 'text-green-400' };
        case 'review':
            return { label: 'Review Needed', color: 'text-yellow-400' };
        case 'pass':
            return { label: 'Likely Pass', color: 'text-red-400' };
        default:
            return { label: 'Unknown', color: 'text-gray-400' };
    }
}
